/**
 * "About this agent" in the machine drawer (WP-15, R-EN-3, R-EN-4): what runs on the machine and
 * why, in the words an IT reviewer asks for. Pure: from what the machine, its fleet status and its
 * trust record report, plus the workspace data setting.
 */
import type { DataSharing, FleetMachine, Machine, NodeTrustInfo } from '@orgus/protocol';

export interface AboutRow {
  label: string;
  value: string;
  /** Paths, names and fingerprints read better in mono. */
  mono?: boolean;
}

type Kind = 'node' | 'source' | 'in-process' | 'unknown';

function kindOf(f: FleetMachine | null): Kind {
  if (!f?.service) return 'unknown';
  if (f.service === 'source') return 'source';
  if (f.service === 'in-process') return 'in-process';
  return 'node';
}

const START: Record<string, string> = {
  'task-scheduler': 'Task Scheduler task OrgusNodeAgent, at sign-in',
  launchd: 'LaunchAgent com.orgus.node-agent, at sign-in',
  systemd: 'systemd user unit orgus-node-agent.service',
  none: 'None: started by hand',
  source: 'None: started from a checkout',
  'in-process': 'Starts with the Orgus server',
};

/** Where the agent keeps its files, per system (the Go agent's per-user install, ADR 0042 decision 8). */
export function agentFolder(m: Pick<Machine, 'os'>, kind: Kind): string {
  if (kind === 'source' || kind === 'in-process') return m.os === 'windows' ? '%USERPROFILE%\\.orgus' : '~/.orgus';
  if (m.os === 'windows') return '%LOCALAPPDATA%\\Orgus';
  if (m.os === 'macos') return '~/Library/Application Support/Orgus';
  return '~/.local/share/orgus';
}

function processOf(m: Machine, kind: Kind): string {
  if (kind === 'source') return `agent-ts ${m.agentVersion} under Node.js, from a checkout`;
  if (kind === 'in-process') return `agent-ts ${m.agentVersion}, inside the Orgus server`;
  return `orgus-node-agent${m.os === 'windows' ? '.exe' : ''} ${m.agentVersion}, as the signed-in user`;
}

function ceilingsOf(t: NodeTrustInfo | null): string {
  if (!t || t.ceilings === null) return 'Not reported yet';
  if (!t.ceilings.length) return 'None: allows what Orgus policy allows';
  return `Refuses ${t.ceilings.join(', ')}`;
}

function dataOf(d: DataSharing | null): string {
  if (!d) return 'Metadata, and transcripts and diffs unless the workspace turns them off';
  return d.content ? 'Metadata, transcripts and diffs, secrets masked' : 'Metadata only: transcripts and diffs stay here';
}

export function aboutAgent(m: Machine, f: FleetMachine | null, t: NodeTrustInfo | null, d: DataSharing | null): AboutRow[] {
  const kind = kindOf(f);
  return [
    { label: 'Process', value: processOf(m, kind) },
    { label: 'Starts', value: f?.service ? (START[f.service] ?? f.service) : 'Not reported yet' },
    { label: 'Folder', value: agentFolder(m, kind), mono: true },
    { label: 'Listens', value: 'Loopback only (127.0.0.1, a random port) for Claude Code hooks. Nothing on the network' },
    { label: 'Connects', value: 'Outbound to Orgus only; no open ports' },
    { label: 'Ceiling', value: ceilingsOf(t) },
    { label: 'Identity', value: t?.fingerprint ? `${t.fingerprint}${t.storage ? ` (${t.storage === 'keystore' ? 'OS keystore' : 'key file'})` : ''}` : 'No machine key yet', mono: !!t?.fingerprint },
    { label: 'Data sent', value: dataOf(d) },
  ];
}
